/**
 * Copy + idioma para InvitacionExclusivaMux por país / Worker / browser.
 * Alineado con WORKER_COUNTRY_TEMPLATE_MAP y getPlaybackIds (en, es, pt, de, fr, it, nl).
 */

import { getWorkerTemplateConfigForCountry } from "@/config/workerCountryTemplateMap";

export type InvitacionExclusivaMuxLocale =
  | "en"
  | "es"
  | "pt"
  | "de"
  | "fr"
  | "it"
  | "nl";

const SPANISH_COUNTRIES = new Set([
  "AR",
  "MX",
  "CL",
  "CO",
  "PE",
  "UY",
  "VE",
  "BO",
  "CR",
  "PA",
  "DO",
  "GT",
  "HN",
  "NI",
  "SV",
  "PY",
  "EC",
  "ES",
]);

const PORTUGUESE_COUNTRIES = new Set(["BR", "PT"]);

/** LU usa fr-LU en el Worker — no incluir aquí (ver invitacionExclusivaMuxCopy). */
const GERMAN_COUNTRIES = new Set(["DE", "AT", "CH"]);

const FRENCH_COUNTRIES = new Set(["FR", "BE", "MC"]);

const ITALIAN_COUNTRIES = new Set(["IT"]);

const DUTCH_COUNTRIES = new Set(["NL"]);

const ENGLISH_COUNTRIES = new Set([
  "US",
  "GB",
  "CA",
  "AU",
  "NZ",
  "IE",
  "SG",
  "PH",
  "IN",
  "ZA",
]);

/** Locale Mux/UI a partir de código ISO2 cuando no hay idioma del Worker. */
export function invitacionExclusivaMuxLocaleFromCountry(
  country: string | null | undefined,
): InvitacionExclusivaMuxLocale {
  const code = (country || "").trim().toUpperCase().slice(0, 2);
  if (!code || code === "UN") return "en";
  if (SPANISH_COUNTRIES.has(code)) return "es";
  if (PORTUGUESE_COUNTRIES.has(code)) return "pt";
  if (GERMAN_COUNTRIES.has(code)) return "de";
  if (FRENCH_COUNTRIES.has(code)) return "fr";
  if (ITALIAN_COUNTRIES.has(code)) return "it";
  if (DUTCH_COUNTRIES.has(code)) return "nl";
  if (ENGLISH_COUNTRIES.has(code)) return "en";
  return "en";
}

/** Locale a partir de BCP47 (ej. de-DE, es-MX, pt-BR). */
export function invitacionExclusivaMuxLocaleFromLanguage(
  language: string | null | undefined,
): InvitacionExclusivaMuxLocale {
  const code = (language || "").trim().toLowerCase().slice(0, 2);
  const allowed: InvitacionExclusivaMuxLocale[] = [
    "en",
    "es",
    "pt",
    "de",
    "fr",
    "it",
    "nl",
  ];
  return allowed.includes(code as InvitacionExclusivaMuxLocale)
    ? (code as InvitacionExclusivaMuxLocale)
    : "en";
}

/**
 * Idioma BCP47 efectivo: Worker (preview ?country=XX) → mapa país → browser → en-US.
 */
export function resolveInvitacionExclusivaMuxLanguage(
  country: string | null | undefined,
  workerLanguage?: string | null,
  browserLang?: string,
): string {
  const code = (country || "").trim().toUpperCase().slice(0, 2);
  const validCode = /^[A-Z]{2}$/.test(code) && code !== "UN";
  const countryIsSpanish = validCode && SPANISH_COUNTRIES.has(code);

  const worker = (workerLanguage || "").trim().replace(/_/g, "-");
  if (worker.length >= 2) {
    const workerIsSpanish = worker.slice(0, 2).toLowerCase() === "es";
    // País de afuera (NO hispano) + Worker en español (default de marca `es-AR`):
    // mostramos el idioma natural del país (en/de/fr/…), no español. Así un
    // visitante de US/GB/etc. ve la página en inglés aunque el Worker mande es.
    if (workerIsSpanish && validCode && !countryIsSpanish) {
      return getWorkerTemplateConfigForCountry(code).language;
    }
    return worker;
  }

  if (validCode) {
    return getWorkerTemplateConfigForCountry(code).language;
  }

  const browser = (browserLang || "").trim();
  if (browser.length >= 2) return browser.replace(/_/g, "-");

  return "en-US";
}

/** Copy del gate de email previo al checkout embebido (captura de leads). */
export type CheckoutEmailGateCopy = {
  emailLabel: string;
  emailPlaceholder: string;
  emailContinue: string;
  emailInvalid: string;
  emailPrivacy: string;
  /**
   * Mensaje de oferta 70% OFF con email (visible al abrir el gate).
   * El visitante elige: email → descuento, o full price sin email.
   */
  emailDiscountOffer: string;
  /** CTA primario cuando la oferta de descuento está visible. */
  emailContinueWithDiscount: string;
  /**
   * CTA secundario: pagar precio completo sin dejar email.
   * El descuento es opt-in; full price siempre está disponible.
   */
  emailSkipFullPrice: string;
  /** Hint corto del “juego”: email = 70%, sin email = full. */
  emailChoiceHint: string;
};

export type InvitacionExclusivaMuxCopy = {
  locale: InvitacionExclusivaMuxLocale;
  language: string;
  ctaLabelDefault: string;
  ctaSubtitle: string;
  loadingShort: string;
  loadingFull: string;
  drawerTitle: string;
  drawerDescription: string;
  checkoutLoading: string;
  emailGate: CheckoutEmailGateCopy;
  /** Label default del CTA secundario "Acceso gratis" (admin puede overridear). */
  freeAccessCtaDefault: string;
  pricePerMonth: (formatted: string) => string;
  /** End-card after the teaser plays once. */
  keepWatchingPrompt: string;
};

export type DualCtaOverlayCopy = {
  membershipTitle: string;
  membershipSubtitle: string;
  primaryCta: string;
  openingCheckout: string;
  freeChannel: string;
  /** Gate de registro: mensaje mostrado antes de abrir el sign-up de Clerk para el canal gratis. */
  freeChannelSignUpPrompt: string;
  privateMessage: string;
  cancelHint: string;
  /**
   * Post-video (plays once, no loop): message above the VIP CTA.
   * e.g. "Para seguir viendo…" / "To keep watching…"
   */
  keepWatchingPrompt: string;
};

export type InvitacionExclusivaMuxSeo = {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  locale: string;
  alternateLocale: string;
};

const EMAIL_GATE_BY_LOCALE: Record<
  InvitacionExclusivaMuxLocale,
  CheckoutEmailGateCopy
> = {
  en: {
    emailLabel: "Your email for 70% OFF",
    emailPlaceholder: "you@email.com",
    emailContinue: "Continue to secure payment",
    emailInvalid: "Enter a valid email.",
    emailPrivacy: "Only for your access and receipt. No spam.",
    emailDiscountOffer: "Leave your email → unlock 70% OFF the first month",
    emailContinueWithDiscount: "Pay with 70% OFF",
    emailSkipFullPrice: "Continue without email · full price",
    emailChoiceHint: "Your choice: email for 70% OFF, or pay full price now.",
  },
  es: {
    emailLabel: "Tu email para 70% OFF",
    emailPlaceholder: "tu@email.com",
    emailContinue: "Continuar al pago seguro",
    emailInvalid: "Ingresá un email válido.",
    emailPrivacy: "Solo para tu acceso y recibo. Sin spam.",
    emailDiscountOffer: "Dejá tu email → desbloqueá 70% OFF el primer mes",
    emailContinueWithDiscount: "Pagar con 70% OFF",
    emailSkipFullPrice: "Continuar sin email · precio completo",
    emailChoiceHint: "Vos elegís: email por 70% OFF, o pagar full ahora.",
  },
  pt: {
    emailLabel: "Seu email para 70% OFF",
    emailPlaceholder: "voce@email.com",
    emailContinue: "Continuar para o pagamento seguro",
    emailInvalid: "Digite um email válido.",
    emailPrivacy: "Só para seu acesso e recibo. Sem spam.",
    emailDiscountOffer: "Deixe seu email → desbloqueie 70% OFF no 1º mês",
    emailContinueWithDiscount: "Pagar com 70% OFF",
    emailSkipFullPrice: "Continuar sem email · preço cheio",
    emailChoiceHint: "Você escolhe: email com 70% OFF, ou full agora.",
  },
  de: {
    emailLabel: "E-Mail für 70% OFF",
    emailPlaceholder: "du@email.com",
    emailContinue: "Weiter zur sicheren Zahlung",
    emailInvalid: "Gib eine gültige E-Mail ein.",
    emailPrivacy: "Nur für Zugang und Beleg. Kein Spam.",
    emailDiscountOffer: "E-Mail angeben → 70% OFF im 1. Monat",
    emailContinueWithDiscount: "Mit 70% OFF zahlen",
    emailSkipFullPrice: "Ohne E-Mail weiter · Vollpreis",
    emailChoiceHint: "Deine Wahl: E-Mail für halben Preis, oder Vollpreis jetzt.",
  },
  fr: {
    emailLabel: "Votre email pour −70%",
    emailPlaceholder: "vous@email.com",
    emailContinue: "Continuer vers le paiement sécurisé",
    emailInvalid: "Entrez un email valide.",
    emailPrivacy: "Uniquement pour votre accès et reçu. Pas de spam.",
    emailDiscountOffer: "Laissez votre email → −70% le 1er mois",
    emailContinueWithDiscount: "Payer avec −70%",
    emailSkipFullPrice: "Continuer sans email · prix plein",
    emailChoiceHint: "À vous: email pour 70% OFF, ou plein tarif maintenant.",
  },
  it: {
    emailLabel: "La tua email per 70% OFF",
    emailPlaceholder: "tu@email.com",
    emailContinue: "Continua al pagamento sicuro",
    emailInvalid: "Inserisci un'email valida.",
    emailPrivacy: "Solo per accesso e ricevuta. Niente spam.",
    emailDiscountOffer: "Lascia l'email → sblocca 70% OFF il 1° mese",
    emailContinueWithDiscount: "Paga con 70% OFF",
    emailSkipFullPrice: "Continua senza email · prezzo pieno",
    emailChoiceHint: "Scegli tu: email a 70% OFF, o full price subito.",
  },
  nl: {
    emailLabel: "Je e-mail voor 70% OFF",
    emailPlaceholder: "jij@email.com",
    emailContinue: "Doorgaan naar veilige betaling",
    emailInvalid: "Voer een geldig e-mailadres in.",
    emailPrivacy: "Alleen voor toegang en bon. Geen spam.",
    emailDiscountOffer: "Laat je e-mail achter → 70% OFF de 1e maand",
    emailContinueWithDiscount: "Betalen met 70% OFF",
    emailSkipFullPrice: "Doorgaan zonder e-mail · volle prijs",
    emailChoiceHint: "Jouw keuze: e-mail voor half prijs, of full price nu.",
  },
};

const FREE_ACCESS_CTA_BY_LOCALE: Record<InvitacionExclusivaMuxLocale, string> = {
  en: "Free access",
  es: "Acceso gratis",
  pt: "Acesso grátis",
  de: "Kostenloser Zugang",
  fr: "Accès gratuit",
  it: "Accesso gratuito",
  nl: "Gratis toegang",
};

const COPY_BY_LOCALE: Record<
  InvitacionExclusivaMuxLocale,
  Omit<
    InvitacionExclusivaMuxCopy,
    "locale" | "language" | "pricePerMonth" | "emailGate" | "freeAccessCtaDefault"
  > & {
    priceSuffix: string;
    dual: DualCtaOverlayCopy;
    seoTitle: (brand: string) => string;
    seoDescription: (brand: string) => string;
  }
> = {
  en: {
    ctaLabelDefault: "Get exclusive access",
    ctaSubtitle: "🔒 Secure Stripe checkout · Cancel anytime",
    loadingShort: "Loading…",
    loadingFull: "Loading",
    drawerTitle: "Exclusive Membership",
    drawerDescription: "Cancel anytime from your account.",
    checkoutLoading: "Loading secure checkout…",
    priceSuffix: "/ month",
    dual: {
      membershipTitle: "Exclusive Membership",
      membershipSubtitle: "Premium content · Monthly subscription",
      primaryCta: "Get exclusive access",
      openingCheckout: "Opening checkout…",
      freeChannel: "Free channel",
      freeChannelSignUpPrompt: "Create your free account to join — one tap and you're in.",
      privateMessage: "Send private message",
      cancelHint: "· Cancel anytime",
      keepWatchingPrompt: "To keep watching…",
    },
    seoTitle: (brand) => `${brand} | Exclusive Membership`,
    seoDescription: (brand) =>
      `Join ${brand}'s exclusive community. Premium video access and secure Stripe checkout.`,
  },
  es: {
    ctaLabelDefault: "Acceder a la membresía",
    ctaSubtitle: "🔒 Pago seguro con Stripe · Cancela cuando quieras",
    loadingShort: "Cargando…",
    loadingFull: "Cargando",
    drawerTitle: "Membresía Exclusiva",
    drawerDescription: "Cancela cuando quieras desde tu cuenta.",
    checkoutLoading: "Cargando checkout seguro…",
    priceSuffix: "/ mes",
    dual: {
      membershipTitle: "Membresía Exclusiva",
      membershipSubtitle: "Contenido premium · Suscripción mensual",
      primaryCta: "Acceder a la membresía",
      openingCheckout: "Abriendo pago…",
      freeChannel: "Canal gratis",
      freeChannelSignUpPrompt: "Creá tu cuenta gratis para unirte — un toque y listo.",
      privateMessage: "Enviar privado",
      cancelHint: "· Cancela cuando quieras",
      keepWatchingPrompt: "Para seguir viendo…",
    },
    seoTitle: (brand) => `${brand} | Membresía Exclusiva`,
    seoDescription: (brand) =>
      `Únete a la comunidad exclusiva de ${brand}. Video premium y pago seguro con Stripe.`,
  },
  pt: {
    ctaLabelDefault: "Acessar a assinatura",
    ctaSubtitle: "🔒 Pagamento seguro com Stripe · Cancele quando quiser",
    loadingShort: "Carregando…",
    loadingFull: "Carregando",
    drawerTitle: "Assinatura Exclusiva",
    drawerDescription: "Cancele quando quiser na sua conta.",
    checkoutLoading: "Carregando checkout seguro…",
    priceSuffix: "/ mês",
    dual: {
      membershipTitle: "Assinatura Exclusiva",
      membershipSubtitle: "Conteúdo premium · Assinatura mensal",
      primaryCta: "Acessar a assinatura",
      openingCheckout: "Abrindo pagamento…",
      freeChannel: "Canal grátis",
      freeChannelSignUpPrompt: "Crie sua conta grátis para entrar — um toque e pronto.",
      privateMessage: "Enviar mensagem privada",
      cancelHint: "· Cancele quando quiser",
      keepWatchingPrompt: "Para continuar assistindo…",
    },
    seoTitle: (brand) => `${brand} | Assinatura Exclusiva`,
    seoDescription: (brand) =>
      `Junte-se à comunidade exclusiva de ${brand}. Vídeo premium e checkout seguro com Stripe.`,
  },
  de: {
    ctaLabelDefault: "Exklusiven Zugang erhalten",
    ctaSubtitle: "🔒 Sichere Stripe-Zahlung · Jederzeit kündbar",
    loadingShort: "Lädt…",
    loadingFull: "Lädt",
    drawerTitle: "Exklusive Mitgliedschaft",
    drawerDescription: "Jederzeit in deinem Konto kündbar.",
    checkoutLoading: "Sicherer Checkout wird geladen…",
    priceSuffix: "/ Monat",
    dual: {
      membershipTitle: "Exklusive Mitgliedschaft",
      membershipSubtitle: "Premium-Inhalte · Monatliches Abo",
      primaryCta: "Exklusiven Zugang erhalten",
      openingCheckout: "Checkout wird geöffnet…",
      freeChannel: "Kostenloser Kanal",
      freeChannelSignUpPrompt: "Erstelle dein kostenloses Konto zum Beitreten — ein Klick genügt.",
      privateMessage: "Private Nachricht senden",
      cancelHint: "· Jederzeit kündbar",
      keepWatchingPrompt: "Um weiterzuschauen…",
    },
    seoTitle: (brand) => `${brand} | Exklusive Mitgliedschaft`,
    seoDescription: (brand) =>
      `Werde Teil von ${brand}s exklusiver Community. Premium-Video und sichere Stripe-Zahlung.`,
  },
  fr: {
    ctaLabelDefault: "Accéder à l'abonnement",
    ctaSubtitle: "🔒 Paiement Stripe sécurisé · Annulez quand vous voulez",
    loadingShort: "Chargement…",
    loadingFull: "Chargement",
    drawerTitle: "Abonnement Exclusif",
    drawerDescription: "Annulez à tout moment depuis votre compte.",
    checkoutLoading: "Chargement du paiement sécurisé…",
    priceSuffix: "/ mois",
    dual: {
      membershipTitle: "Abonnement Exclusif",
      membershipSubtitle: "Contenu premium · Abonnement mensuel",
      primaryCta: "Accéder à l'abonnement",
      openingCheckout: "Ouverture du paiement…",
      freeChannel: "Canal gratuit",
      freeChannelSignUpPrompt: "Crée ton compte gratuit pour rejoindre — un tap et c'est parti.",
      privateMessage: "Envoyer un message privé",
      cancelHint: "· Annulez quand vous voulez",
      keepWatchingPrompt: "Pour continuer à regarder…",
    },
    seoTitle: (brand) => `${brand} | Abonnement Exclusif`,
    seoDescription: (brand) =>
      `Rejoignez la communauté exclusive de ${brand}. Vidéo premium et paiement Stripe sécurisé.`,
  },
  it: {
    ctaLabelDefault: "Accedi all'abbonamento",
    ctaSubtitle: "🔒 Pagamento Stripe sicuro · Annulla quando vuoi",
    loadingShort: "Caricamento…",
    loadingFull: "Caricamento",
    drawerTitle: "Abbonamento Esclusivo",
    drawerDescription: "Annulla quando vuoi dal tuo account.",
    checkoutLoading: "Caricamento checkout sicuro…",
    priceSuffix: "/ mese",
    dual: {
      membershipTitle: "Abbonamento Esclusivo",
      membershipSubtitle: "Contenuti premium · Abbonamento mensile",
      primaryCta: "Accedi all'abbonamento",
      openingCheckout: "Apertura checkout…",
      freeChannel: "Canale gratuito",
      freeChannelSignUpPrompt: "Crea il tuo account gratuito per entrare — un tocco e sei dentro.",
      privateMessage: "Invia messaggio privato",
      cancelHint: "· Annulla quando vuoi",
      keepWatchingPrompt: "Per continuare a guardare…",
    },
    seoTitle: (brand) => `${brand} | Abbonamento Esclusivo`,
    seoDescription: (brand) =>
      `Unisciti alla community esclusiva di ${brand}. Video premium e checkout Stripe sicuro.`,
  },
  nl: {
    ctaLabelDefault: "Exclusieve toegang",
    ctaSubtitle: "🔒 Veilige Stripe-checkout · Altijd opzegbaar",
    loadingShort: "Laden…",
    loadingFull: "Laden",
    drawerTitle: "Exclusief Lidmaatschap",
    drawerDescription: "Altijd opzegbaar via je account.",
    checkoutLoading: "Veilige checkout laden…",
    priceSuffix: "/ maand",
    dual: {
      membershipTitle: "Exclusief Lidmaatschap",
      membershipSubtitle: "Premium content · Maandelijks abonnement",
      primaryCta: "Exclusieve toegang",
      openingCheckout: "Checkout openen…",
      freeChannel: "Gratis kanaal",
      freeChannelSignUpPrompt: "Maak je gratis account aan om mee te doen — één tik en je bent binnen.",
      privateMessage: "Privébericht sturen",
      cancelHint: "· Altijd opzegbaar",
      keepWatchingPrompt: "Om verder te kijken…",
    },
    seoTitle: (brand) => `${brand} | Exclusief Lidmaatschap`,
    seoDescription: (brand) =>
      `Word lid van de exclusieve community van ${brand}. Premium video en veilige Stripe-checkout.`,
  },
};

const OG_LOCALE_BY_MUX_LOCALE: Record<
  InvitacionExclusivaMuxLocale,
  { locale: string; alternateLocale: string }
> = {
  en: { locale: "en_US", alternateLocale: "es_ES" },
  es: { locale: "es_ES", alternateLocale: "en_US" },
  pt: { locale: "pt_BR", alternateLocale: "en_US" },
  de: { locale: "de_DE", alternateLocale: "en_US" },
  fr: { locale: "fr_FR", alternateLocale: "en_US" },
  it: { locale: "it_IT", alternateLocale: "en_US" },
  nl: { locale: "nl_NL", alternateLocale: "en_US" },
};

function resolveInvitacionExclusivaMuxLocale(
  country: string | null | undefined,
  language: string,
  workerLanguage?: string | null,
): InvitacionExclusivaMuxLocale {
  if ((workerLanguage || "").trim().length >= 2) {
    return invitacionExclusivaMuxLocaleFromLanguage(language);
  }
  return invitacionExclusivaMuxLocaleFromCountry(country);
}

/** Todos los labels por defecto (para no pisar override admin al cambiar país). */
export const INVITACION_MUX_DEFAULT_CTA_LABELS = new Set(
  Object.values(COPY_BY_LOCALE).map((c) => c.ctaLabelDefault),
);

export function invitacionExclusivaMuxCopy(
  country: string | null | undefined,
  options?: {
    workerLanguage?: string | null;
    browserLang?: string;
  },
): InvitacionExclusivaMuxCopy {
  const language = resolveInvitacionExclusivaMuxLanguage(
    country,
    options?.workerLanguage,
    options?.browserLang,
  );
  const locale = resolveInvitacionExclusivaMuxLocale(
    country,
    language,
    options?.workerLanguage,
  );
  const block = COPY_BY_LOCALE[locale] ?? COPY_BY_LOCALE.en;

  return {
    locale,
    language,
    ctaLabelDefault: block.ctaLabelDefault,
    ctaSubtitle: block.ctaSubtitle,
    loadingShort: block.loadingShort,
    loadingFull: block.loadingFull,
    drawerTitle: block.drawerTitle,
    drawerDescription: block.drawerDescription,
    checkoutLoading: block.checkoutLoading,
    emailGate: EMAIL_GATE_BY_LOCALE[locale] ?? EMAIL_GATE_BY_LOCALE.en,
    freeAccessCtaDefault:
      FREE_ACCESS_CTA_BY_LOCALE[locale] ?? FREE_ACCESS_CTA_BY_LOCALE.en,
    pricePerMonth: (formatted) => `${formatted} ${block.priceSuffix}`.trim(),
    keepWatchingPrompt: block.dual.keepWatchingPrompt,
  };
}

/** Idioma para cadena Mux VIP (getPlaybackIds). */
export function invitacionExclusivaMuxPlaybackLanguage(
  country: string | null | undefined,
  workerLanguage?: string | null,
  browserLang?: string,
): string {
  return resolveInvitacionExclusivaMuxLanguage(
    country,
    workerLanguage,
    browserLang,
  );
}

/** Copy overlay DualCTAVideoLanding (GB y otros países). */
export function dualCtaOverlayCopy(
  country: string | null | undefined,
  options?: {
    workerLanguage?: string | null;
    browserLang?: string;
  },
): DualCtaOverlayCopy & { locale: InvitacionExclusivaMuxLocale } {
  const base = invitacionExclusivaMuxCopy(country, options);
  const block = COPY_BY_LOCALE[base.locale] ?? COPY_BY_LOCALE.en;
  return { locale: base.locale, ...block.dual };
}

/** SEO home / funnel internacional por país e idioma Worker. */
export function invitacionExclusivaMuxSeo(
  country: string | null | undefined,
  brandName: string,
  options?: {
    workerLanguage?: string | null;
    browserLang?: string;
  },
): InvitacionExclusivaMuxSeo {
  const base = invitacionExclusivaMuxCopy(country, options);
  const block = COPY_BY_LOCALE[base.locale] ?? COPY_BY_LOCALE.en;
  const og = OG_LOCALE_BY_MUX_LOCALE[base.locale] ?? OG_LOCALE_BY_MUX_LOCALE.en;
  const title = block.seoTitle(brandName);
  const description = block.seoDescription(brandName);
  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    locale: og.locale,
    alternateLocale: og.alternateLocale,
  };
}

export function dualCtaFreeChannelLabel(
  locale: InvitacionExclusivaMuxLocale,
  adminLabel?: string | null,
): string {
  if (adminLabel && adminLabel.trim()) return adminLabel.trim();
  const defaults: Record<InvitacionExclusivaMuxLocale, string> = {
    en: "Free channel",
    es: "Canal gratis",
    pt: "Canal grátis",
    de: "Kostenloser Kanal",
    fr: "Canal gratuit",
    it: "Canale gratuito",
    nl: "Gratis kanaal",
  };
  return defaults[locale] || defaults.en;
}

/** Formato precio checkout (USD intl / ARS AR). */
export function formatInvitacionMuxCheckoutPrice(
  currency: "ARS" | "USD",
  amountCents: number,
): string {
  const amount = amountCents / 100;
  if (currency === "USD") return `$${amount.toFixed(2)} USD`;
  return `$${amount.toLocaleString("es-AR")} ARS`;
}